import { z } from "zod";

import { multiPolygonBBox, pointInMultiPolygon, type BBox } from "@/map/geo.ts";

/**
 * SF district polygons: their shape on disk, and the spatial index the filter
 * hit-tests against. Both live here because they're the same concern — the
 * script bakes this file's shape, and everything downstream reads it.
 *
 * The whole collection is parsed on load, but geometry is checked *shallowly*:
 * the tag plus the presence of a coordinates array catches the realistic bake
 * failure (Polygon emitted where MultiPolygon was meant), whereas walking ~50k
 * positions on every load to re-prove what the bake script already guaranteed
 * would cost far more than it protects.
 */

export type DistrictSet = z.infer<typeof DistrictSetSchema>;
export const DistrictSetSchema = z.enum(["neighborhood", "supervisor"]);

export type DistrictProps = z.infer<typeof DistrictPropsSchema>;
export const DistrictPropsSchema = z.object({
  /** Slug used as the stable id in persisted state, e.g. "neighborhood:mission". */
  id: z.string(),
  name: z.string(),
  /** Which baked set it came from. */
  set: DistrictSetSchema,
});

/** O(1) per feature — see the note above on why the positions go unwalked. */
const MultiPolygonSchema = z.custom<GeoJSON.MultiPolygon>(
  (value) =>
    typeof value === "object" &&
    value !== null &&
    (value as GeoJSON.MultiPolygon).type === "MultiPolygon" &&
    Array.isArray((value as GeoJSON.MultiPolygon).coordinates),
  { message: "expected a MultiPolygon geometry" },
);

export type DistrictFeature = z.infer<typeof DistrictFeatureSchema>;
export const DistrictFeatureSchema = z.object({
  type: z.literal("Feature"),
  /** The bake writes the slug here as well as in properties. Declared because
   *  `z.object` strips what it doesn't know, and MapLibre reads feature-level
   *  `id` for feature-state — dropping it would break a hover highlight later
   *  in a way that looks nothing like a schema problem. */
  id: z.union([z.string(), z.number()]).optional(),
  properties: DistrictPropsSchema,
  geometry: MultiPolygonSchema,
});

export type DistrictCollection = z.infer<typeof DistrictCollectionSchema>;
export const DistrictCollectionSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(DistrictFeatureSchema),
});

/**
 * A district with its bounding box precomputed. Hit-testing thousands of points
 * against 41 neighbourhoods is only cheap because the bbox rejects almost
 * everything before the ray cast runs.
 */
export interface IndexedDistrict {
  id: string;
  name: string;
  props: DistrictProps;
  geometry: GeoJSON.MultiPolygon;
  bbox: BBox;
}

export function indexDistricts(features: DistrictFeature[]): IndexedDistrict[] {
  return features.map((f) => ({
    id: f.properties.id,
    name: f.properties.name,
    props: f.properties,
    geometry: f.geometry,
    bbox: multiPolygonBBox(f.geometry),
  }));
}

/** Whether a point falls inside any of `districts`. */
export function inAnyDistrict(lon: number, lat: number, districts: IndexedDistrict[]): boolean {
  return districtAt(lon, lat, districts) !== null;
}

/** Which district contains a point, if any. */
export function districtAt(
  lon: number,
  lat: number,
  districts: IndexedDistrict[],
): IndexedDistrict | null {
  for (const d of districts) {
    if (lon < d.bbox[0] || lon > d.bbox[2] || lat < d.bbox[1] || lat > d.bbox[3]) continue;
    if (pointInMultiPolygon(lon, lat, d.geometry)) return d;
  }
  return null;
}
